// Filename: PrivacyPolicyPage.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50">
                <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
                    <Link to="/" className="flex items-center">
                        <img
                            src="/appsytec-logo.png"
                            alt="Appsytec Logo"
                            className="h-10 w-auto"
                        />
                    </Link>
                    <Link to="/" className="text-gray-700 font-medium hover:text-red-500 transition-colors">
                        Home
                    </Link>
                </div>
            </nav>

            <main className="flex-grow pt-24 pb-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-lg max-w-none">
                        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
                        
                        <p className="text-gray-600 mb-8">
                            <strong>Last updated:</strong> January 2025
                        </p>

                        <div className="space-y-8">
                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    Appsytec Pty Ltd ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or interact with us. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Personal Information</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    We may collect personal information that you provide directly to us, including:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                                    <li>Name, email address, phone number, and contact details</li>
                                    <li>Resume, CV, and employment history</li>
                                    <li>Professional qualifications and certifications</li>
                                    <li>Payment and billing information</li>
                                    <li>Feedback, testimonials, and survey responses</li>
                                </ul>
                                
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Automatically Collected Information</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    When you visit our website, we automatically collect certain information:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                                    <li>IP address and browser information</li>
                                    <li>Device type and operating system</li>
                                    <li>Pages viewed and time spent on our website</li>
                                    <li>Referring website or source</li>
                                    <li>Cookies and similar tracking technologies</li>
                                </ul>

                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Third-Party Authentication</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    If you sign in using Google, we may receive basic profile information such as your name, email address, and profile photo. This information is used solely for authentication and account management purposes and is not used for advertising.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    We use the information we collect for various purposes, including:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                                    <li>Providing recruitment and consulting services</li>
                                    <li>Matching candidates with suitable job opportunities</li>
                                    <li>Communicating with clients and candidates</li>
                                    <li>Processing applications and managing client portals</li>
                                    <li>Improving our website and services</li>
                                    <li>Conducting market research and analytics</li>
                                    <li>Complying with legal obligations</li>
                                    <li>Preventing fraud and ensuring security</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    We may share your information in the following circumstances:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                                    <li><strong>With Clients:</strong> We share candidate information with potential employers for recruitment purposes</li>
                                    <li><strong>Service Providers:</strong> Third-party vendors who assist with our operations</li>
                                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                                    <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                                    <li><strong>Consent:</strong> When you have given explicit consent</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    We implement appropriate technical and organisational security measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    We retain personal information for as long as necessary to fulfil the purposes outlined in this privacy policy, unless a longer retention period is required or permitted by law. Candidate information may be retained for future opportunities with consent.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Under Australian Privacy Principles, you have the right to:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                                    <li>Access your personal information</li>
                                    <li>Correct inaccurate or incomplete information</li>
                                    <li>Request deletion of your information</li>
                                    <li>Object to processing in certain circumstances</li>
                                    <li>Withdraw consent where processing is based on consent</li>
                                    <li>Lodge a complaint with the Office of the Australian Information Commissioner</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies and Tracking</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    Our website uses cookies and similar tracking technologies to enhance user experience, analyse website traffic, and for marketing purposes. You can manage your cookie preferences through our cookie consent banner or your browser settings. For more information, please see our Cookie Policy.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Links</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to Privacy Policy</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last updated" date. Your continued use of our services after any modifications constitutes acceptance of the updated policy.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Information</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    If you have any questions about this Privacy Policy or our data practices, please contact us:
                                </p>
                                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                                    <p className="text-gray-700"><strong>Appsytec Pty Ltd</strong></p>
                                    <p className="text-gray-700">Email: contact@appsytec.com</p>
                                    <p className="text-gray-700">Location: Melbourne, Australia</p>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-8 h-8 bg-red-500 rounded-full"></div>
                            <span className="ml-2 text-xl font-bold">Appsytec Pty Ltd</span>
                        </div>
                        <p className="text-gray-400 mb-4">© 2025 Appsytec Pty Ltd. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PrivacyPolicyPage;